import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
  Skeleton
} from '@mui/material';
import { MdOutlineCall, MdApproval } from 'react-icons/md';
import { BiPlus } from 'react-icons/bi';
import SidebarItem from './SidebarItem';
import { IoChevronDownSharp } from 'react-icons/io5';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAgent } from 'providers/context/AgentProvider';
import { useGetAgents } from 'apis/business/agent/hook';
import * as urls from 'routes/config';
import { WEB_EMBED_SCRIPT } from 'env';
import PerformanceIcon from 'assets/icons/PerformanceIcon';
import CallLogsIcon from 'assets/icons/CallLogsIcon';
import ConfigureIcon from 'assets/icons/ConfigureIcon';
import { CacheKeys } from 'utils/cacheKeys';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { queryClient } from 'providers/AllProviders';
import { deleteAgent } from 'apis/business/agent/api';
import WorksheetIcon from 'assets/icons/Worksheet';
import TalkButton from '../../Common/TalkButton';
import SignalsIcon from 'assets/icons/SignalsIcon';
import { IoIosLink } from 'react-icons/io';
import LiveModeToggleBox from '@/components/Common/LiveModeToggleBox';
import BusinessDropdown from '@/components/Layouts/components/BusinessDropdown';
import LocationDropdown from '@/components/Layouts/components/LocationDropdown';
import SettingIcon from '@/assets/icons/SettingIcon';
import { useBusiness } from '@/providers/context/BusinessProvider';
import { RxCross2 } from 'react-icons/rx';
import DeleteAgentModal from '@/components/Common/DeleteAgentModal';
import ChatLogsIcon from '@/assets/icons/ChatLogsIcon';

interface Props {
  isMenuOpen?: boolean;
  handleClose?: () => void;
}

export default function Sidebar({ isMenuOpen, handleClose }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { locationId, agentId } = useParams();
  const { selectedBusiness } = useBusiness();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const { setSelectedAgent, setSelectedAgentId } = useAgent();
  const { data: agents, isLoading: loadingAgent } = useGetAgents(null, { per_page: 9999, filter: { s: 'name asc' } });
  const open = Boolean(anchorEl);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<any>(null);

  useEffect(() => {
    const filteredAgent = agents?.data?.find((item) => item.id === Number(agentId));
    const agentIndex = agents?.data?.findIndex((item) => item.id === Number(agentId));

    setSelectedIndex(agentIndex !== -1 ? Number(agentIndex) : 0);
    setSelectedAgent(filteredAgent ?? agents?.data[0]);
  }, [agents, setSelectedAgent, setSelectedIndex, agentId]);

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (_: React.MouseEvent<HTMLElement>, index: number) => {
    setSelectedAgent(agents?.data?.[index]);
    setSelectedAgentId(agents?.data?.[index]?.id);
    localStorage.setItem(CacheKeys.selectedAgentId, String(agents?.data?.[index]?.id));
    setSelectedIndex(index);
    navigate(`${urls.agentBase(locationId, agents?.data?.[index]?.id)}`);
    queryClient.resetQueries();
    setAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const existingScript = document.querySelector(`script[src="${WEB_EMBED_SCRIPT}"]`);
    if (!existingScript) {
      const script1 = document.createElement('script');
      script1.src = WEB_EMBED_SCRIPT ?? '';
      script1.id = 'embed-script';
      script1.async = true;

      document.head.appendChild(script1);

      return () => {
        document.head.removeChild(script1);
      };
    }
  }, []);

  const handleDeleteAgent = async (id: number) => {
    const response = await deleteAgent(locationId!, id);
    if ((response as any)?.meta?.message) {
      queryClient.invalidateQueries({
        queryKey: ['agents', locationId],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['agent', locationId, id],
        refetchType: 'active'
      });
      const leftAgents = agents?.data.filter((item) => item.id !== id);
      if (leftAgents?.length) {
        setSelectedAgent(leftAgents?.[0]);
        setSelectedAgentId(leftAgents?.[0]?.id);
        localStorage.setItem(CacheKeys.selectedAgentId, String(leftAgents?.[0]?.id));
        setSelectedIndex(0);
        navigate(`${urls.agentBase(locationId, leftAgents?.[0]?.id)}/${urls.home.configure}`);
      } else {
        setSelectedAgent(undefined);
        setSelectedAgentId(undefined);
        localStorage.removeItem(CacheKeys.selectedAgentId);
        localStorage.removeItem(CacheKeys.agentIds);
        navigate(`${urls.base(locationId)}/${urls.onBoarding.create}`);
      }
    }
  };

  const agentSelectionSkeleton = (
    <Box marginX={2} marginBottom={2} maxHeight='121px'>
      <Box
        sx={{
          border: '1px solid #ccc',
          borderRadius: 2,
          padding: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Skeleton variant='text' width='60%' height={24} />
          <Skeleton variant='circular' width={20} height={20} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant='circular' width={16} height={16} />
          <Skeleton variant='text' width='40%' height={16} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant='circular' width={16} height={16} />
          <Skeleton variant='text' width='50%' height={16} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant='circular' width={16} height={16} />
          <Skeleton variant='text' width='50%' height={16} />
        </Box>
      </Box>
    </Box>
  );

  const drawerContent = (
    <Box display='flex' flexDirection='column' height='calc(100vh - 64px)' justifyContent='space-between'>
      <List sx={{ flexGrow: 1, paddingTop: 1 }}>
        {isMediumScreen && (
          <Box px={2} pb={2} borderBottom='1px solid #EAEAEA' bgcolor={'#F8F9FA'}>
            <Box display='flex' alignItems='center' gap={2}>
              {selectedBusiness && (
                <Link to={urls.business.base}>
                  <IconButton
                    color='primary'
                    sx={{
                      background: theme.palette.primary.light,
                      color: theme.palette.primary.main,
                      borderRadius: 0.5,
                      padding: { xs: '4px', lg: '8px' }
                    }}
                  >
                    <Box display='flex'>
                      <SettingIcon />
                    </Box>
                  </IconButton>
                </Link>
              )}
              <BusinessDropdown />
              <Box>
                <RxCross2 size={18} onClick={handleClose} />
              </Box>
            </Box>
            <Box mt={1.5}>
              <LocationDropdown />
            </Box>
          </Box>
        )}
        <Box>
          {/* --- Agent Section --- */}
          {locationId === undefined || loadingAgent ? (
            agentSelectionSkeleton
          ) : (
            <Box marginX={2}>
              <Box>
                <List component='nav' aria-label='Device settings'>
                  <ListItemButton
                    id='lock-button'
                    aria-haspopup='listbox'
                    aria-controls='lock-menu'
                    aria-label='when device is locked'
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClickListItem}
                    sx={{
                      border: '1px solid #ECECEC',
                      borderRadius: 2,
                      display: 'block',
                      maxHeight: '121px'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ListItemText>
                        <Typography variant='h6' component='p' noWrap title={agents?.data?.[selectedIndex]?.name}>
                          {agents?.data?.[selectedIndex]?.name}
                        </Typography>
                      </ListItemText>
                      <IoChevronDownSharp />
                    </Box>
                    {agents?.data?.[selectedIndex]?.integration_name && (
                      <Box color={theme.palette.secondary.main} display='flex' alignItems='start' gap='4px' mt={0.5}>
                        <Box height='16px' width='16px'>
                          <IoIosLink />
                        </Box>
                        <Typography variant='body2' color={theme.palette.secondary.main}>
                          {agents?.data?.[selectedIndex]?.integration_name}
                        </Typography>
                      </Box>
                    )}
                    <Box color={theme.palette.secondary.main} display='flex' alignItems='start' gap='4px'>
                      <Box height='16px' width='16px'>
                        <MdApproval />
                      </Box>
                      <Typography variant='body2' color={theme.palette.secondary.main} textTransform='capitalize'>
                        {agents?.data?.[selectedIndex]?.agent_interaction}
                      </Typography>
                    </Box>
                    <Box color={theme.palette.secondary.main} display='flex' alignItems='start' gap='4px' mt={0.5}>
                      <Box height='16px' width='16px'>
                        <MdOutlineCall />
                      </Box>
                      <Typography variant='body2' color={theme.palette.secondary.main}>
                        {agents?.data?.[selectedIndex]?.phone_number ?? '---'}
                      </Typography>
                    </Box>
                  </ListItemButton>
                </List>
                <Menu
                  id='lock-menu'
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleMenuClose}
                  MenuListProps={{
                    'aria-labelledby': 'lock-button',
                    role: 'listbox'
                  }}
                  slotProps={{ paper: { sx: { width: 205 } } }}
                >
                  <Box
                    display='flex'
                    justifyContent='center'
                    gap={0.5}
                    alignItems='center'
                    border='1px solid #F3F3F3'
                    color={theme.palette.secondary.main}
                    margin={1}
                    paddingY={0.75}
                    borderRadius={0.5}
                    sx={{ textDecoration: 'none' }}
                    component={Link}
                    to={`${urls.base(locationId)}/${urls.onBoarding.selectAgent}`}
                    onClick={() => handleMenuClose()}
                  >
                    <BiPlus />
                    <Typography>Add Agent</Typography>
                  </Box>
                  {agents?.data
                    .map((option, index) => (
                      <MenuItem
                        key={option.id}
                        selected={index === selectedIndex}
                        onClick={(event) => handleMenuItemClick(event, index)}
                      >
                        <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
                          <Typography
                            sx={{
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis'
                            }}
                            title={option.name}
                          >
                            {option.name}
                          </Typography>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setAgentToDelete(option);
                              setDeleteModalOpen(true);
                            }}
                          >
                            <RiDeleteBin6Line style={{ color: 'red', fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </MenuItem>
                    ))
                    .sort()}
                </Menu>
              </Box>
            </Box>
          )}

          <ListItem>
            <TalkButton />
          </ListItem>

          {/* --- Navigation --- */}
          <SidebarItem to={urls.home.performance} icon={<PerformanceIcon />} label='Performance' />
          <SidebarItem to={urls.home.callLogs} icon={<CallLogsIcon />} label='Call Logs' />
          <SidebarItem to={urls.home.chatLogs} icon={<ChatLogsIcon size={24} />} label='Chat Logs' />
          <SidebarItem to={urls.home.signals} icon={<SignalsIcon />} label='Signals' />
          <SidebarItem to={urls.worksheetBase(locationId, agentId)} icon={<WorksheetIcon />} label='Worksheet' />
          <SidebarItem
            to={
              agentId
                ? `${urls.agentBase(locationId, agentId)}/${urls.home.configure}`
                : `${urls.base(locationId)}/${urls.home.configure}`
            }
            icon={<ConfigureIcon />}
            label='Configure'
          />
        </Box>
      </List>
      {/* Bottom Live Mode box */}
      <LiveModeToggleBox />
    </Box>
  );

  return (
    <>
      <Drawer
        variant={isMediumScreen ? 'temporary' : 'permanent'}
        open={isMediumScreen ? isMenuOpen : true}
        onClose={handleClose}
        anchor='left'
        sx={{
          width: 240,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 240,
            boxSizing: 'border-box',
            top: isMediumScreen ? 0 : '65px',
            backgroundColor: theme.palette.background.paper
          }
        }}
        ModalProps={{
          keepMounted: true
        }}
      >
        {drawerContent}
      </Drawer>
      <DeleteAgentModal
        open={deleteModalOpen}
        agentName={agentToDelete?.name || ''}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => {
          handleDeleteAgent(agentToDelete.id);
          setDeleteModalOpen(false);
        }}
      />
    </>
  );
}
